import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Settings2 } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { toast } from '@/components/ui/toaster'

interface CustomField {
  id: string
  name: string
  key: string
  type: string
  options?: string[]
  entityType: string
  isRequired: boolean
  orderIndex: number
}

const fieldTypes = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select (Dropdown)' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'url', label: 'URL' },
]

const entityTypes = [
  { value: 'candidate', label: 'Candidates' },
  { value: 'job', label: 'Jobs' },
  { value: 'application', label: 'Applications' },
]

export function CustomFieldsManager() {
  const queryClient = useQueryClient()
  const [showDialog, setShowDialog] = useState(false)
  const [editingField, setEditingField] = useState<CustomField | null>(null)
  const [filterEntity, setFilterEntity] = useState<string>('candidate')

  // Form state
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [type, setType] = useState('text')
  const [entityType, setEntityType] = useState('candidate')
  const [isRequired, setIsRequired] = useState(false)
  const [options, setOptions] = useState('')

  const { data: fields, isLoading } = useQuery({
    queryKey: ['custom-fields', filterEntity],
    queryFn: () => api.get<CustomField[]>(`/custom-fields?entityType=${filterEntity}`),
  })

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post('/custom-fields', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] })
      toast({ title: 'Custom field created', variant: 'success' })
      closeDialog()
    },
    onError: () => {
      toast({ title: 'Failed to create field', variant: 'destructive' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/custom-fields/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] })
      toast({ title: 'Custom field updated', variant: 'success' })
      closeDialog()
    },
    onError: () => {
      toast({ title: 'Failed to update field', variant: 'destructive' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/custom-fields/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] })
      toast({ title: 'Custom field deleted', variant: 'success' })
    },
  })

  const closeDialog = () => {
    setShowDialog(false)
    setEditingField(null)
    setName('')
    setKey('')
    setType('text')
    setEntityType('candidate')
    setIsRequired(false)
    setOptions('')
  }

  const openEdit = (field: CustomField) => {
    setEditingField(field)
    setName(field.name)
    setKey(field.key)
    setType(field.type)
    setEntityType(field.entityType)
    setIsRequired(field.isRequired)
    setOptions(field.options?.join(', ') || '')
    setShowDialog(true)
  }

  const handleSave = () => {
    const data: Record<string, unknown> = {
      name,
      key: key || name.toLowerCase().replace(/\s+/g, '_'),
      type,
      entityType,
      isRequired,
    }
    if (type === 'select' && options) {
      data.options = options.split(',').map((o) => o.trim()).filter(Boolean)
    }

    if (editingField) {
      updateMutation.mutate({ id: editingField.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Custom Fields</CardTitle>
          </div>
          <CardDescription>
            Define additional fields for candidates, jobs, and applications
          </CardDescription>
        </div>
        <Dialog open={showDialog} onOpenChange={(v) => { if (!v) closeDialog(); else setShowDialog(true) }}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add Field
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingField ? 'Edit Custom Field' : 'Create Custom Field'}</DialogTitle>
              <DialogDescription>
                Define a custom field to capture additional data
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    if (!editingField) {
                      setKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))
                    }
                  }}
                  placeholder="Years of Experience"
                />
              </div>
              <div className="space-y-2">
                <Label>Field Key</Label>
                <Input
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="years_experience"
                  className="font-mono text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Field Type</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {fieldTypes.map((ft) => (
                        <SelectItem key={ft.value} value={ft.value}>
                          {ft.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Applies To</Label>
                  <Select value={entityType} onValueChange={setEntityType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {entityTypes.map((et) => (
                        <SelectItem key={et.value} value={et.value}>
                          {et.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {type === 'select' && (
                <div className="space-y-2">
                  <Label>Options (comma separated)</Label>
                  <Input
                    value={options}
                    onChange={(e) => setOptions(e.target.value)}
                    placeholder="Junior, Mid, Senior, Lead"
                  />
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label>Required field</Label>
                <Switch checked={isRequired} onCheckedChange={setIsRequired} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={!name || createMutation.isPending || updateMutation.isPending}
              >
                {editingField ? 'Save Changes' : 'Create Field'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-4">
          {entityTypes.map((et) => (
            <Badge
              key={et.value}
              variant={filterEntity === et.value ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setFilterEntity(et.value)}
            >
              {et.label}
            </Badge>
          ))}
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 skeleton rounded-md" />
            ))}
          </div>
        ) : (!fields || fields.length === 0) ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No custom fields for {entityTypes.find((et) => et.value === filterEntity)?.label.toLowerCase()}.
          </p>
        ) : (
          <div className="space-y-2">
            {fields.map((field: CustomField) => (
              <div
                key={field.id}
                className="flex items-center justify-between p-3 border rounded-lg group hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-medium text-sm">{field.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{field.key}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{field.type}</Badge>
                  {field.isRequired && (
                    <Badge variant="secondary" className="text-xs">Required</Badge>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => openEdit(field)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => {
                        if (confirm('Delete this custom field? All stored values will be lost.')) {
                          deleteMutation.mutate(field.id)
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
